import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

// AbortController para cancelar peticiones duplicadas
let controller = null;

// ===============================
// PRODUCTS ENDPOINTS
// ===============================
/**
 * Obtener todos los productos de un cliente
 * @param {string} clientId - ID del cliente
 * @param {object} filters - Filtros opcionales (name, sku, active, category_id)
 * @returns {Promise} Respuesta con la lista de productos
 */
export const getProducts = async (clientId, filters = {}) => {
  try {
    // Construir query string con filtros    
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/products${queryString ? `?${queryString}` : ''}`;

    const response = await axiosWithAuth.get(url, {
      headers: {
        'Content-Type': 'application/json'
      },
    });
    
    controller = null;
    
    return {
      success: true,
      data: response.data,
      message: 'Productos obtenidos exitosamente'
    };
  } catch (error) {
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      console.log('Request aborted');
      return {
        success: false,
        data: null,
        message: 'Request aborted'
      };
    } else {
      console.error('Error getting products:', error);
    }
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al obtener productos'
    };
  }
};

/**
 * Obtener un producto específico por su ID
 * @param {string} clientId - ID del cliente
 * @param {string} productId - ID del producto
 * @returns {Promise} Respuesta con los datos del producto
 */
export const getProductById = async (clientId, productId) => {
  try {
 

    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/products/${productId}`, {
      headers: {
         'Content-Type': 'application/json'
      }
    });

    return {
      success: true,
      data: response.data,
      message: 'Producto obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error getting product by ID:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al obtener producto'
    };
  }
};

/**
 * Crear un nuevo producto
 * @param {string} clientId - ID del cliente
 * @param {object} productData - Datos del producto a crear
 * @returns {Promise} Respuesta con el producto creado
 */
export const createProduct = async (clientId, productData) => {
  try {
 

    const response = await axiosWithAuth.post(`${API_URL}/workflows/p2p/${clientId}/products`, productData, {
      headers: {
         'Content-Type': 'application/json'
      }
    });

    return {
      success: true,
      data: response.data,
      message: 'Producto creado exitosamente'
    };
  } catch (error) {
    console.error('Error creating product:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al crear producto'
    };
  }
};

/**
 * Actualizar un producto existente
 * @param {string} clientId - ID del cliente
 * @param {string} productId - ID del producto a actualizar
 * @param {object} productData - Datos actualizados del producto
 * @returns {Promise} Respuesta con el producto actualizado
 */
export const updateProduct = async (clientId, productId, productData) => {
  try {
 

    const response = await axiosWithAuth.put(`${API_URL}/workflows/p2p/${clientId}/products/${productId}`, productData, {
      headers: {
         'Content-Type': 'application/json'
      }
    });

    return {
      success: true,
      data: response.data,
      message: 'Producto actualizado exitosamente'
    };
  } catch (error) {
    console.error('Error updating product:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al actualizar producto'
    };
  }
};

/**
 * Eliminar un producto
 * @param {string} clientId - ID del cliente
 * @param {string} productId - ID del producto a eliminar
 * @param {object} additionalData - Datos adicionales para la eliminación
 * @returns {Promise} Respuesta de la eliminación
 */
export const deleteProduct = async (clientId, productId, additionalData = {}) => {
  try {
 

    const response = await axiosWithAuth.delete(`${API_URL}/workflows/p2p/${clientId}/products/${productId}`, {
      headers: {
         'Content-Type': 'application/json'
      },
      data: additionalData
    });

    return {
      success: true,
      data: response.data,
      message: 'Producto eliminado exitosamente'
    };
  } catch (error) {
    console.error('Error deleting product:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al eliminar producto'
    };
  }
};

/**
 * Obtener items disponibles para asociar a productos (desde inventario)
 * @param {string} clientId - ID del cliente
 * @param {object} filters - Filtros opcionales (name, sku)
 * @returns {Promise} Respuesta con la lista de items disponibles
 */
export const getAvailableItems = async (clientId, filters = {}) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('inventoryLoading', { detail: true });
    window.dispatchEvent(event);

    // Construir query string con filtros
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/inventory${queryString ? `?${queryString}` : ''}`;

    const response = await axiosWithAuth.get(url, {
      headers: {
         'Content-Type': 'application/json'
      }
    });

    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('inventoryLoading', { detail: false });
    window.dispatchEvent(endEvent);

    return {
      success: true,
      data: response.data,
      message: 'Items de inventario obtenidos exitosamente'
    };
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('inventoryLoading', { detail: false });
    window.dispatchEvent(endEvent);

    console.error('Error getting inventory items:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al obtener items de inventario'
    };
  }
};

/**
 * Buscar productos por término de búsqueda
 * @param {string} clientId - ID del cliente
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Promise} Respuesta con los productos encontrados
 */
export const searchProducts = async (clientId, searchTerm) => {
  try {
    const filters = {
      name: searchTerm
    };
    return await getProducts(clientId, filters);
  } catch (error) {
    console.error('Error searching products:', error);
    return {
      success: false,
      data: null,
      message: error.message || 'Error en la búsqueda de productos'
    };
  }
};

/**
 * Obtener estadísticas de productos
 * @param {string} clientId - ID del cliente
 * @returns {Promise} Respuesta con estadísticas de productos
 */
export const getProductsStats = async (clientId) => {
  try {
 

    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/products/stats`, {
      headers: {
         'Content-Type': 'application/json'
      }
    });

    return {
      success: true,
      data: response.data,
      message: 'Estadísticas obtenidas exitosamente'
    };
  } catch (error) {
    console.error('Error getting products stats:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Error al obtener estadísticas'
    };
  }
};

// ===============================
// EXPORT DEFAULT
// ===============================

const mantisFlowsProducts = {
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  getAvailableItems,
  searchProducts,
  getProductsStats
};

export default mantisFlowsProducts;
